import React, { useState } from 'react';
import { MoreHorizontal, Loader2, Check, X, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { DMTimestampTriangleIcon } from '@/components/shared/display/Icons';

interface ChipOption {
  value: string;
  label: string;
  enabled?: boolean;
  disabled?: boolean;
  source?: 'user' | 'Datamind';
  badge?: 'user' | string;
  canDelete?: boolean;
  markerId?: string;
  timestamp?: Date;
  color?: string;
  isLoading?: boolean;
}

interface ChipsWithMoreProps {
  filterId: string;
  options: ChipOption[];
  currentValue: string | string[];
  isMultiSelect?: boolean;
  onFilterChange: (filterId: string, value: string[], filter?: any) => void;
  onDelete?: (value: string) => void;
  filter?: any;
  visibleChipsLimit?: number; // How many chips to show before collapsing
  isLoading?: boolean;
  moreLabel?: string; // Label for the "More" button (default: "More")
}

/**
 * ChipsWithMore - Shows first N chips, then collapses rest into "More" dropdown
 * Based on the feature badges design from DataLayerToolbar
 */
export const ChipsWithMore: React.FC<ChipsWithMoreProps> = ({
  filterId,
  options,
  currentValue,
  isMultiSelect = false,
  onFilterChange,
  onDelete,
  filter,
  visibleChipsLimit = 2,
  isLoading = false,
  moreLabel = 'More',
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const currentArray = Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : []);

  // Split options into visible and extra
  const visibleOptions = options.slice(0, visibleChipsLimit);
  const extraOptions = options.slice(visibleChipsLimit);
  const activeExtraOptions = extraOptions.filter((opt) => currentArray.includes(opt.value));
  const hasActiveExtraOptions = activeExtraOptions.length > 0;

  const handleChipClick = (optionValue: string) => {
    const option = options.find(opt => opt.value === optionValue);
    if (!option || option.disabled || isLoading) return;

    const activeCount = currentArray.length;
    const isActive = currentArray.includes(optionValue);
    const isLastActive = isActive && activeCount === 1;

    // Prevent disabling the last active option in multi-select mode
    if (isMultiSelect && isLastActive) return;

    let newValue;
    if (isMultiSelect === false) {
      // Single select: toggle or select
      newValue = isActive ? [] : [optionValue];
    } else {
      // Multi select: toggle in array
      newValue = isActive
        ? currentArray.filter((v) => v !== optionValue)
        : [...currentArray, optionValue];
    }

    onFilterChange(filterId, newValue, filter);
  };

  const renderBadgeIcon = (option: ChipOption) => {
    if (option.badge === 'user' || option.source === 'user') {
      return <User className="w-3 h-3" />;
    }
    if (option.source === 'Datamind') {
      return <DMTimestampTriangleIcon />;
    }
    return null;
  };

  const renderChip = (option: ChipOption) => {
    const isActive = currentArray.includes(option.value);
    const activeCount = currentArray.length;
    const isLastActive = isActive && activeCount === 1;
    const isDisabled = option.disabled || (isMultiSelect && isLastActive) || isLoading;
    const isThisLoading = option.isLoading || false;

    return (
      <Badge
        key={option.value}
        className={`px-3 py-1 text-xs bg-secondary flex items-center gap-1 border-0 ${
          isActive ? 'bg-primary-500 text-white hover:bg-primary-500' : 'cursor-pointer hover:bg-secondary'
        } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        onClick={() => {
          if (!isDisabled) {
            handleChipClick(option.value);
          }
        }}
      >
        {isThisLoading && <Loader2 className="w-3 h-3 animate-spin" />}
        {renderBadgeIcon(option)}
        {option.label}
        {option.canDelete && onDelete && (
          <button
            className="ml-1 hover:bg-red-500/20 rounded-full p-0.5"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(option.value);
            }}
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </Badge>
    );
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Visible chips */}
      {visibleOptions.map(renderChip)}

      {/* "More" button with dropdown */}
      {extraOptions.length > 0 && (
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <div className="relative">
              <button
                className={`cursor-pointer px-3 py-1.5 pb-1 text-xs flex items-center bg-secondary rounded-full font-semibold gap-1.5 ${
                  hasActiveExtraOptions ? '!bg-primary-500 text-white' : ''
                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                disabled={isLoading}
              >
                {hasActiveExtraOptions
                  ? `${activeExtraOptions[0].label}${
                      activeExtraOptions.length > 1 ? ` +${activeExtraOptions.length - 1}` : ''
                    }`
                  : moreLabel}
                <MoreHorizontal size={14} />
              </button>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2 space-y-2">
            {extraOptions.map((option) => {
              const isActive = currentArray.includes(option.value);
              const isThisLoading = option.isLoading || false;
              const activeCount = currentArray.length;
              const isLastActive = isActive && activeCount === 1;
              const isDisabled = option.disabled || (isMultiSelect && isLastActive) || isLoading;

              return (
                <div
                  key={option.value}
                  className={`flex items-center justify-between w-full gap-2 ${
                    isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                  }`}
                  onClick={() => {
                    if (!isDisabled) {
                      handleChipClick(option.value);
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    {isMultiSelect ? (
                      <Checkbox
                        id={`chip-${option.value}`}
                        checked={isActive}
                        disabled={isDisabled}
                        onCheckedChange={(checked: boolean) => {
                          if (!isDisabled) {
                            handleChipClick(option.value);
                          }
                        }}
                      />
                    ) : (
                      <Check className={`w-4 h-4 text-primary-500 ${isActive ? 'opacity-100' : 'opacity-0'}`} />
                    )}
                    {isThisLoading && <Loader2 className="w-3 h-3 animate-spin text-primary-500" />}
                    {renderBadgeIcon(option)}
                    <label
                      htmlFor={`chip-${option.value}`}
                      className={`text-sm whitespace-nowrap ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {option.label}
                    </label>
                  </div>
                  {option.canDelete && onDelete && (
                    <button
                      className="hover:bg-red-500/20 rounded-full p-0.5 flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(option.value);
                      }}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
